import { Environment } from '@x/common/environment';
import { streamlineRegular } from '@x/common/icon/packs/streamline-icons-regular';
import { GOOGLE_MAPS_CONFIG } from '@x/ecommerce-shop/config/map-config';
import { IShopConfig } from '@x/ecommerce-shop/config/shop.config';
import { THE_NEWT_BRAND } from '@x/ecommerce-shop/flavours/thenewt/config/brand';

export const getShopConfig = (env: Environment): IShopConfig => ({
  production: env.requireBool('NG_PRODUCTION'),
  flavor: 'tn',
  brand: THE_NEWT_BRAND,
  defaultLocale: 'en-GB',
  meta: {
    titleSuffix: THE_NEWT_BRAND.name,
    defaultMeta: {
      metaTitle: 'Welcome',
      metaDescription:
        'Our passionate team of farmers, gardeners, butchers, bakers, cheesemakers, chefs and cyder makers work in harmony with land and livestock to produce delicious food for the table.',
      metaImage: `https://res.cloudinary.com/${env.requireStr('NG_CLOUDINARY')}/image/fetch/v1662362337/https://storage.googleapis.com/mediabucket-prod-00fecdb9/babylonstoren/image/053e18c89e99c7d72a3a3a35911410f67db8.jpg`,
    },
  },
  cloudinary: {
    cloudName: env.requireStr('NG_CLOUDINARY'),
  },
  ngxs: {
    developmentMode: !env.requireBool('NG_PRODUCTION'),
  },
  defaultCountry: {
    name: 'United Kingdom',
    dialCode: '44',
    code: 'GB',
  },
  ecommerceBackend: {
    baseUrl: env.requireStr('NG_APP_URL') + '/ecommerce/backend',
  },
  ecommerceShopClient: {
    graphUrl: env.requireStr('NG_APP_URL') + '/ecommerce/graphql',
  },
  contentClient: {
    graphUrl: env.requireStr('NG_APP_URL') + '/content/graphql',
  },
  geocodeClient: {
    graphUrl: env.requireStr('NG_APP_URL') + '/geocode/graphql',
  },
  money: {
    format: {
      currencyDisplay: 'narrowSymbol',
      // only available in newer browsers
      // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#trailingzerodisplay
      trailingZeroDisplay: 'stripIfInteger',
    } as any,
  },
  contactInfo: {
    email: 'shop@thenewtinsomerset.com',
    phone: '+44 (0) 19 6357 7727',
    availability: 'Available Monday - Sunday from 09h00 - 17h00',
  },
  barcode: {
    url: 'https://europe-west1-ecommerce-283509.cloudfunctions.net/x-barcode-svg-stage',
  },
  date: {
    format: 'd MMM, y',
  },
  iconConfig: {
    fixedWidth: true,
    defaultPrefix: 'fal',
    packs: [streamlineRegular],
  },
  storageConfig: { prefix: 'x_thenewt_ecommerce_shop_', version: 1 },
  features: {
    availability: {
      useDefaultRegionFromChannel: true,
      showDeliveryRegionBadges: false,
      showUnavailableSlots: true,
      availabilityStatus: {
        AVAILABLE_SOON: {
          minDays: 3,
        },
        PRE_ORDER: {
          minDays: 14,
        },
        LOW_STOCK_URGENCY: {
          maxDays: 7,
          maxQuantity: 3,
        },
        LAST_CHANCE_AVAILABILITY: {
          maxDays: 7,
        },
      },
    },
    productListingViewMoreMode: 'detailed',
    deliveryFeeMsg: 'Free shipping when you spend £40 or more',
    productViewMobileStickyActionBar: false,
    deliverySlotCalendar: true,
    enableNeedHelpChat: false,
    newsletterOptIn: {
      onRegistration: true,
      enabledByDefault: true,
      text: 'I would like to receive news and exclusive offers from The Newt in Somerset. ( View our Privacy Policy )',
      policy: {
        textToMatch: 'Privacy Policy',
        absoluteUrl: 'https://thenewtinsomerset.com/privacy-policy',
      },
    },

    mainMenu: {
      taxonTargetLevel: [0, 1, 2],
    },
    menuTree: {
      taxonTargetLevel: [0, 1],
      nodeAutoClose: true,
    },
    addressing: {
      instructionsMaxLength: 35,
    },
  },
  googleMaps: {
    ...GOOGLE_MAPS_CONFIG,
    defaultCoordinates: {
      lat: 51.0812007,
      lng: -2.4933919,
    },
  },
  externalShopLink: {
    code: 'za',
    name: 'South Africa',
    url: 'https://shop.babylonstoren.com',
    defaultCountry: 'ZA',
  },
  referral: {
    mentionMe: {
      partnerCode: 'mmf2d0395b',
      api: env.requireStr('NG_REFERRAL_MENTION_ME'),
    },
  },
  tracking: {
    algolia: {
      appId: env.requireStr('NG_ALGOLIA_APP_ID'),
      apiKey: env.requireStr('NG_ALGOLIA_API_KEY'),
    },
    logRocket: {
      appId: env.requireStr('NG_LOGROCKET'),
    },
    googleTagManager: {
      containerId: 'GTM-57C35KP',
    },
  },
  abTestConfig: {
    apiHost: env.optStr('NG_GROWTH_BOOK_API_HOST'),
    clientKey: env.optStr('NG_GROWTH_BOOK_CLIENT_KEY'),
    enableDevMode: env.bool('NG_GROWTH_BOOK_ENABLE_DEBUG', undefined),
    features: {
      'payment-options': {
        name: 'ecommerce-payment-options-tn',
        options: {
          showByDefault: false,
        },
      },
    },
  },
  browser: {
    cookies: {
      prefix: 'tn_',
      domain: 'localhost',
      secure: true,
      path: '/',
    },
    scripts: [
      {
        name: 'google-maps',
        src: 'https://maps.googleapis.com/maps/api/js?key=AIzaSyCxKH3-upo3yLjFguZvJufUZ5P-qxN2fhs&callback=Function.prototype&loading=async',
        defer: true,
        async: true,
      },
      {
        name: 'story-block',
        src: [
          'https://asset.fwcdn3.com/js/storyblock.js',
          'https://asset.fwcdn3.com/js/fwn.js',
          'https://asset.fwcdn3.com/js/embed-feed.js',
        ],
        defer: true,
        async: true,
      },
    ],
  },
});
